import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import ErrorMessage from 'ViatoUi/Shared/Feedback/ErrorMessage/ErrorMessage';
import Checkbox from 'ViatoUi/Web/FormElements/Checkbox';
import Input from 'ViatoUi/Web/FormElements/Inputs/Input';
import InputDate from 'ViatoUi/Web/FormElements/Inputs/InputDate';
import Select from 'ViatoUi/Web/FormElements/Selects/Select';
import Textarea from 'ViatoUi/Web/FormElements/Textarea';

import styles from './Field.module.scss';

const Field = ({
	placeholder,
	value,
	onChange,
	showErrorMessage,
	errorMessages,
	required,
	type,
	options,
	inputSettings,
	tooltip,
	onMount,
	onUnmount
}) => {
	const user = useSelector(contractGeneratorSelectors.getUser);

	const getDateFieldSettings = setting => {
		const date = new Date();

		if (setting) {
			if (setting.scale === 'month') {
				if (setting.type === 'plus') {
					date.setMonth(date.getMonth() + setting.amount);
				} else {
					date.setMonth(date.getMonth() - setting.amount);
				}
			} else if (setting.type === 'plus') {
				date.setDate(date.getDate() + setting.amount);
			} else {
				date.setDate(date.getDate() - setting.amount);
			}

			if (
				Array.isArray(setting.possibleDays) &&
				setting.possibleDays.length > 0 &&
				!setting.possibleDays.includes(parseInt(date.getDate()))
			) {
				const currentDay = parseInt(date.getDate());
				let daysToAdd = 0;
				for (let index = 0; index < 32; index += 1) {
					if (setting.possibleDays.includes(currentDay + index) && daysToAdd === 0) {
						daysToAdd = index;
					}
				}

				date.setDate(date.getDate() + daysToAdd);
			}
		}

		return date;
	};

	const timestampToDate = timestamp => {
		const date = new Date(timestamp);
		return date;
	};

	const dateToString = date => {
		const month = date.getMonth() + 1;
		const day = date.getDate();

		return `${day > 9 ? day : `0${day}`}.${month > 9 ? month : `0${month}`}.${date.getFullYear()}`;
	};

	const getField = () => {
		switch (type) {
			case 'textarea':
				return <Textarea label={placeholder} value={value} onChange={newValue => onChange(newValue)} />;

			case 'select':
				return (
					<Select
						className={styles.field}
						label={placeholder}
						options={options}
						required={required}
						value={value.value}
						onChange={newValue => onChange(newValue)}
					/>
				);

			case 'inputDate':
				return (
					<Input
						className={styles.field}
						customInput={
							<InputDate
								date={
									typeof value === 'object' ? timestampToDate(value.key) : getDateFieldSettings(inputSettings.initalDate)
								}
								excludedDays={user.isLoggedIn ? [] : inputSettings.excludedDays}
								maxDate={user.isLoggedIn ? null : getDateFieldSettings(inputSettings.maxDate)}
								minDate={user.isLoggedIn ? null : getDateFieldSettings(inputSettings.minDate)}
								onChange={newDate => onChange({ value: dateToString(newDate), key: newDate.getTime() })}
							/>
						}
						placeholder={placeholder}
						required={required}
						tooltip={tooltip}
						value='value'
					/>
				);

			case 'checkbox':
				return <Checkbox checked={value === '' ? false : value} label={placeholder} onToggle={() => onChange(!value)} />;

			default:
				return (
					<Input
						className={styles.field}
						placeholder={placeholder}
						required={required}
						tooltip={tooltip}
						value={value}
						onChange={newValue => onChange(newValue)}
					/>
				);
		}
	};

	useEffect(() => {
		onMount();
		if (type === 'inputDate' && typeof value !== 'object') {
			onChange({
				value: dateToString(getDateFieldSettings(inputSettings.initalDate)),
				key: getDateFieldSettings(inputSettings.initalDate).getTime()
			});
		}

		return () => {
			if (document.getElementById('viato-contract-generator-forms') !== null) {
				onUnmount();
			}
		};
	}, []);

	return (
		<>
			{getField()}
			{showErrorMessage && <ErrorMessage errorMessages={errorMessages} />}
		</>
	);
};

Field.propTypes = {
	placeholder: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object, PropTypes.bool]).isRequired,
	options: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	showErrorMessage: PropTypes.bool.isRequired,
	onMount: PropTypes.func.isRequired,
	onUnmount: PropTypes.func.isRequired,
	type: PropTypes.string.isRequired,
	errorMessages: PropTypes.array,
	inputSettings: PropTypes.object,
	required: PropTypes.bool,
	tooltip: PropTypes.string
};

Field.defaultProps = {
	errorMessages: [],
	options: [],
	inputSettings: {},
	required: false,
	tooltip: null
};

export default Field;
